import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton, DropdownElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './correspondence-tab.scss';

// Store entities
import {
  getAvailableCorrespondenceTime, updateAvailableCorrespondenceTime
} from '../../../../store/session-settings/session-settings.thunks';

// Models
import { ICorrespondenceTimes } from '../../../../interfaces/session-settings.interfaces';

export type TCorrespondenceTabRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TCorrespondenceTabProps = {
  correspondence: ICorrespondenceTimes;
};

export const CorrespondenceTab = forwardRef<TCorrespondenceTabRef, TCorrespondenceTabProps>((
    {correspondence}: TCorrespondenceTabProps, ref
  ) => {
    const [form] = Form.useForm<{
      correspondence_message_time: string;
    }>();

    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [savingChanges, setSavingChanges] = useState<boolean>(false);

    const dispatch = useDispatch<any>();

    const timeOptions: {
      value: string,
      label: string
    }[] = correspondence.correspondence_available_time?.map((duration) => ({
      value: `${duration}`, label: `${duration} ${duration === 1 ? 'hr' : 'hrs'}`
    }));

    const saveChanges = async (): Promise<void> => {
      const {correspondence_message_time} = form.getFieldsValue();
      setSavingChanges(true);
      const {payload} = await dispatch(updateAvailableCorrespondenceTime(correspondence_message_time));
      setSavingChanges(false);
      if (!payload) return;
      setHasChanges(false);
      form.resetFields();
    }

    const onValuesChange = (_: {correspondence_message_time: string}, {correspondence_message_time}: {
      correspondence_message_time: string
    }): void => {
      setHasChanges(correspondence_message_time !== correspondence.correspondence_message_time.toString());
    }

    const loadCorrespondenceSettings = (): void => {
      dispatch(getAvailableCorrespondenceTime());
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldValue('correspondence_message_time', correspondence.correspondence_message_time.toString());
        setHasChanges(false);
        setSavingChanges(false);
      }
    }));

    useEffect(() => {
      loadCorrespondenceSettings();
    }, []);

    return (
      <div className="correspondence-tab">
        {/*<div className="correspondence-tab--label business--label label-title">*/}
        {/*  Response Time*/}
        {/*</div>*/}
        <div className="correspondence-tab--description business--description">
          In this section, you can set the time frame within which Consultants must respond to Clients' text-based
          requests. The minimum response time is 1 hour. <br/>
          Choose a duration that aligns with your business needs and the Consultants' capability to respond within the
          specified period.
        </div>
        <div className="correspondence-tab--description business--description">
          Important: If a Consultant fails to respond within the set time frame, the Client's payment is refunded, and the
          Consultant can no longer respond to that request. However, Consultants can still send system notifications to
          alert Clients of their availability. Clients can then resubmit their request, triggering a new response time for
          the Consultant.
        </div>
        <Form className="correspondence-tab--body business--body-items" form={form} onValuesChange={onValuesChange}>
          <div className="body-item">
            <div className="body-item--select">
              {!!correspondence.correspondence_message_time && <Form.Item name="correspondence_message_time"
                                                                          initialValue={correspondence.correspondence_message_time.toString()}>
                <DropdownElement options={timeOptions}
                                 withoutAll={true}
                                 placeholder={'Duration'}/>
              </Form.Item>}
            </div>
            <div className="body-item--action" hidden={!hasChanges}>
              <CustomButton loading={savingChanges}
                            text={'Save'}
                            size={'small'}
                            onClick={saveChanges}/>
            </div>
          </div>
        </Form>
      </div>
    );
  }
)
